import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Upload } from "lucide-react";
import { useState } from "react";
import { getMe } from "@/lib/core.functions";
import { Button } from "@/components/ui/button";
import { LastSessionImportDialog, type LastSessionCourse } from "@/components/LastSessionImportDialog";
import { listAllSessions } from "@/lib/schedule.functions";
import { listSpilloverBlocks, listHolidays, listCollabDays } from "@/lib/holidays.functions";
import { expandHolidayDates } from "@/lib/holidays";
import { listCourses } from "@/lib/courses.functions";
import { PageHeader } from "@/components/AppShell";
import { ScheduleCalendar, type CalendarSession } from "@/components/ScheduleCalendar";

export const Route = createFileRoute("/_authenticated/calendar")({
  head: () => ({
    meta: [
      { title: "Calendar | Leadership Edge Scheduling" },
      { name: "description", content: "Month view of every scheduled course session across all timeframes." },
      { property: "og:title", content: "Calendar | Leadership Edge Scheduling" },
      { property: "og:description", content: "Month view of every scheduled course session across all timeframes." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: CalendarPage,
});

function CalendarPage() {
  const fetchSessions = useServerFn(listAllSessions);
  const { data, isLoading, error } = useQuery({
    queryKey: ["all-sessions"],
    queryFn: () => fetchSessions(),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });
  const fetchSpillover = useServerFn(listSpilloverBlocks);
  const live = { refetchOnMount: "always" as const, refetchOnWindowFocus: true, refetchInterval: 30_000 };
  const spill = useQuery({ queryKey: ["spillover-blocks"], queryFn: () => fetchSpillover(), ...live });
  const fetchHolidays = useServerFn(listHolidays);
  const holidaysQ = useQuery({ queryKey: ["holidays"], queryFn: () => fetchHolidays(), ...live });
  const fetchCollab = useServerFn(listCollabDays);
  const collabQ = useQuery({ queryKey: ["collab-days"], queryFn: () => fetchCollab(), ...live });
  const holidaySessions: CalendarSession[] = (holidaysQ.data ?? []).flatMap((h) =>
    expandHolidayDates([h], 2027, 2030)
      .filter((d) => d >= "2027-01-01")
      .map((d) => ({
        id: `holiday-${h.id}-${d}`,
        start_date: d,
        end_date: d,
        start_time: "00:00:00",
        end_time: "23:59:00",
        trainer_id: "holiday",
        meeting_dates: [d],
        courses: { name: `Holiday: ${h.name}` },
        trainers: { full_name: "No sessions" },
      })),
  );
  const collabSessions: CalendarSession[] = (collabQ.data ?? []).map((c) => ({
    id: `collab-${c.id}`,
    start_date: c.collab_date,
    end_date: c.collab_date,
    start_time: c.start_time,
    end_time: c.end_time,
    trainer_id: "collab",
    meeting_dates: [c.collab_date],
    courses: { name: `Collab Day: ${c.name}` },
    trainers: { full_name: "Blocked" },
  }));
  // Calendar shows only spillover sessions (from the Holidays page), from Jan 2027 on.
  const spillSessions: CalendarSession[] = (spill.data ?? []).filter((b) => b.block_date >= "2027-01-01").map((b) => ({
    id: `spillover-${b.id}`,
    start_date: b.block_date,
    end_date: b.block_date,
    start_time: b.start_time,
    end_time: b.end_time,
    trainer_id: "spillover",
    meeting_dates: [b.block_date],
    courses: { name: `Spillover: ${b.title}` },
    trainers: { full_name: "Spillover" },
  }));
  // Each course's last session (from the Courses page) — the scheduler counts the next run from it.
  const fetchCourses = useServerFn(listCourses);
  const coursesQ = useQuery({ queryKey: ["courses"], queryFn: () => fetchCourses(), refetchOnMount: "always" });
  const lastSessions: CalendarSession[] = ((coursesQ.data?.courses ?? []) as {
    id: string; name: string; last_virtual_session_date: string | null; last_virtual_session_time: string | null;
  }[])
    .filter((c) => c.last_virtual_session_date)
    .map((c) => ({
      id: `last-${c.id}`,
      start_date: c.last_virtual_session_date!,
      end_date: c.last_virtual_session_date!,
      start_time: c.last_virtual_session_time ?? "00:00:00",
      end_time: c.last_virtual_session_time ?? "00:00:00",
      trainer_id: "spillover",
      meeting_dates: [c.last_virtual_session_date!],
      courses: { name: `Last session: ${c.name}` },
      trainers: null,
    }));

  const fetchMe = useServerFn(getMe);
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => fetchMe() });
  const [importOpen, setImportOpen] = useState(false);
  const [focusMonth, setFocusMonth] = useState("2027-01-01");

  return (
    <>
      <PageHeader eyebrow="Calendar" title="Calendar" description="Every scheduled course session across all timeframes." />
      {me?.isAdmin ? (
        <div className="mb-4 flex justify-end">
          <Button variant="soft" size="sm" onClick={() => setImportOpen(true)}>
            <Upload /> Upload last session dates
          </Button>
          <LastSessionImportDialog
            courses={(coursesQ.data?.courses ?? []) as LastSessionCourse[]}
            open={importOpen}
            onOpenChange={setImportOpen}
            onImported={(dates) => { void coursesQ.refetch(); const d = [...dates].sort()[0]; if (d) setFocusMonth(`${d.slice(0, 7)}-01`); }}
          />
        </div>
      ) : null}
      {isLoading || spill.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading sessions…
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      ) : (
        <ScheduleCalendar
          sessions={[...((data ?? []) as CalendarSession[]), ...spillSessions, ...holidaySessions, ...collabSessions]}
          key={focusMonth}
          defaultMonth={focusMonth}
        />
      )}
    </>
  );
}
